import {
  decodeBase64Url,
  decryptAesGcm,
  encodeBase64Url,
  encryptAesGcm,
  importAesKey,
  packEncryptedBlob,
  randomBytes,
  unpackEncryptedBlob,
} from './primitives';
import { uuidToBytes } from './recovery';
import {
  decryptVaultPlaintext,
  mapVaultUnlockCryptoFailure,
  type VaultPlaintext,
} from './vault';

const DB_NAME = 'shareweb-device-vault';
const DB_VERSION = 1;
const STORE_NAME = 'enrollments';
const DEVICE_WRAP_PREFIX = 'shareweb:vkwrap-device:v1';

interface PrfExtensionResults {
  prf?: {
    enabled?: boolean;
    results?: {
      first?: ArrayBuffer;
    };
  };
}

interface DeviceEnrollmentRecord {
  userId: string;
  credentialId: string;
  prfSalt: string;
  vkWrap: string;
}

export function buildDeviceWrapAad(userId: string, credentialId: string): string {
  return `${DEVICE_WRAP_PREFIX}|${userId}|${credentialId}`;
}

export async function wrapVkForDevice(
  prfSecret: Uint8Array,
  userId: string,
  credentialId: string,
  vaultKey: Uint8Array,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(prfSecret);
  const aad = new TextEncoder().encode(buildDeviceWrapAad(userId, credentialId));
  return packEncryptedBlob(await encryptAesGcm(aesKey, vaultKey, aad));
}

export async function unwrapVkFromDevice(
  prfSecret: Uint8Array,
  userId: string,
  credentialId: string,
  vkWrap: Uint8Array,
): Promise<Uint8Array> {
  const aesKey = await importAesKey(prfSecret);
  const aad = new TextEncoder().encode(buildDeviceWrapAad(userId, credentialId));
  return decryptAesGcm(aesKey, unpackEncryptedBlob(vkWrap), aad);
}

function openDeviceDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'userId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexeddb_open_failed'));
  });
}

async function readEnrollment(userId: string): Promise<DeviceEnrollmentRecord | null> {
  const db = await openDeviceDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const request = transaction.objectStore(STORE_NAME).get(userId);
    request.onsuccess = () => resolve((request.result as DeviceEnrollmentRecord | undefined) ?? null);
    request.onerror = () => reject(request.error ?? new Error('indexeddb_read_failed'));
    transaction.oncomplete = () => db.close();
  });
}

async function writeEnrollment(record: DeviceEnrollmentRecord): Promise<void> {
  const db = await openDeviceDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const request = transaction.objectStore(STORE_NAME).put(record);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('indexeddb_write_failed'));
    transaction.oncomplete = () => db.close();
  });
}

async function deleteEnrollment(userId: string): Promise<void> {
  const db = await openDeviceDb();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const request = transaction.objectStore(STORE_NAME).delete(userId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error ?? new Error('indexeddb_delete_failed'));
    transaction.oncomplete = () => db.close();
  });
}

export async function isDeviceRememberSupported(): Promise<boolean> {
  if (typeof window === 'undefined') {
    return false;
  }

  if (!window.PublicKeyCredential || !navigator.credentials?.create || !navigator.credentials?.get) {
    return false;
  }

  // Prefer capability discovery when available. Older browsers may lack
  // getClientCapabilities while still supporting PRF — fall through and let
  // enrollment fail closed on prf.enabled / prf results.
  const capabilities = await PublicKeyCredential.getClientCapabilities?.();
  if (capabilities) {
    return capabilities['extension:prf'] === true;
  }

  return true;
}

/** Algorithms accepted for local passkey creation. Include RS256 for Windows Hello (esp. Firefox). */
const PASSKEY_PUB_KEY_CRED_PARAMS: PublicKeyCredentialParameters[] = [
  { type: 'public-key', alg: -7 }, // ES256
  { type: 'public-key', alg: -257 }, // RS256 (Windows Hello)
];

export async function hasDeviceRememberEnrollment(userId: string): Promise<boolean> {
  try {
    return (await readEnrollment(userId)) !== null;
  } catch {
    return false;
  }
}

function extractPrfSecret(results: PrfExtensionResults): Uint8Array {
  const buffer = results.prf?.results?.first;
  if (!buffer || buffer.byteLength !== 32) {
    throw new Error('prf_not_supported');
  }

  return new Uint8Array(buffer);
}

function asPublicKeyCredential(
  credential: Credential | null,
  failureCode: 'webauthn_create_failed' | 'webauthn_get_failed',
): PublicKeyCredential {
  // Prefer duck-typing over instanceof — Firefox can fail instanceof checks across realms.
  if (
    !credential
    || credential.type !== 'public-key'
    || !('rawId' in credential)
    || !(credential as PublicKeyCredential).rawId
    || typeof (credential as PublicKeyCredential).getClientExtensionResults !== 'function'
  ) {
    throw new Error(failureCode);
  }

  return credential as PublicKeyCredential;
}

function rethrowWebAuthnFailure(cause: unknown, fallbackCode: string): never {
  if (cause instanceof DOMException) {
    // User dismissed the platform prompt.
    if (cause.name === 'NotAllowedError' || cause.name === 'AbortError') {
      throw new Error('webauthn_cancelled');
    }
    // Firefox on Windows often reports PRF / Hello incompatibility as UnknownError
    // ("The operation failed for an unknown transient reason") instead of enabled:false.
    if (
      cause.name === 'UnknownError'
      || cause.name === 'NotSupportedError'
      || cause.name === 'SecurityError'
      || cause.name === 'InvalidStateError'
    ) {
      throw new Error('prf_not_supported');
    }
  }

  if (cause instanceof Error) {
    throw cause;
  }

  throw new Error(fallbackCode);
}

async function createPasskeyCredential(userId: string): Promise<PublicKeyCredential> {
  let credential: Credential | null;
  try {
    credential = await navigator.credentials.create({
      publicKey: {
        challenge: randomBytes(32) as BufferSource,
        rp: {
          name: 'ShareWeb',
          id: window.location.hostname,
        },
        user: {
          id: uuidToBytes(userId) as BufferSource,
          name: userId,
          displayName: 'ShareWeb',
        },
        pubKeyCredParams: PASSKEY_PUB_KEY_CRED_PARAMS,
        authenticatorSelection: {
          userVerification: 'required',
          residentKey: 'preferred',
        },
        extensions: {
          // Signal PRF intent. Some platforms (notably Windows Hello) only prove PRF
          // on a later assertion, so do not require prf.enabled here.
          prf: {},
        },
        timeout: 60_000,
      },
    });
  } catch (cause) {
    rethrowWebAuthnFailure(cause, 'webauthn_create_failed');
  }

  return asPublicKeyCredential(credential, 'webauthn_create_failed');
}

async function assertPasskeyWithPrf(
  credentialId: Uint8Array,
  prfSalt: Uint8Array,
): Promise<Uint8Array> {
  let assertion: Credential | null;
  try {
    assertion = await navigator.credentials.get({
      publicKey: {
        challenge: randomBytes(32) as BufferSource,
        rpId: window.location.hostname,
        allowCredentials: [{ type: 'public-key', id: credentialId as BufferSource }],
        userVerification: 'required',
        extensions: {
          prf: {
            eval: {
              first: prfSalt as BufferSource,
            },
          },
        },
        timeout: 60_000,
      },
    });
  } catch (cause) {
    rethrowWebAuthnFailure(cause, 'webauthn_get_failed');
  }

  return extractPrfSecret(
    asPublicKeyCredential(assertion, 'webauthn_get_failed').getClientExtensionResults() as PrfExtensionResults,
  );
}

export async function enrollDeviceRemember(userId: string, vaultKey: Uint8Array): Promise<void> {
  if (!(await isDeviceRememberSupported())) {
    throw new Error('prf_not_supported');
  }

  const credential = await createPasskeyCredential(userId);
  const credentialId = encodeBase64Url(new Uint8Array(credential.rawId));
  const prfSalt = randomBytes(32);
  const prfSecret = await assertPasskeyWithPrf(new Uint8Array(credential.rawId), prfSalt);
  const vkWrap = await wrapVkForDevice(prfSecret, userId, credentialId, vaultKey);

  await writeEnrollment({
    userId,
    credentialId,
    prfSalt: encodeBase64Url(prfSalt),
    vkWrap: encodeBase64Url(vkWrap),
  });
}

async function unlockVaultFromKey(
  vaultKey: Uint8Array,
  vaultBlob: Uint8Array,
  expectedServerVersion: number,
): Promise<{ vaultKey: Uint8Array; plaintext: VaultPlaintext; userPrivateKey: Uint8Array }> {
  try {
    const plaintext = await decryptVaultPlaintext(vaultKey, vaultBlob);

    if (plaintext.vaultVersion !== expectedServerVersion) {
      throw new Error('vault_version_mismatch');
    }

    if (typeof plaintext.userPriv !== 'string' || plaintext.userPriv.length === 0) {
      throw new Error('vault_missing_user_priv');
    }

    return {
      vaultKey,
      plaintext,
      userPrivateKey: decodeBase64Url(plaintext.userPriv),
    };
  } catch (cause) {
    mapVaultUnlockCryptoFailure(cause);
  }
}

export async function unlockVaultWithDeviceRemember(
  userId: string,
  vaultBlob: Uint8Array,
  expectedServerVersion: number,
): Promise<{ vaultKey: Uint8Array; plaintext: VaultPlaintext; userPrivateKey: Uint8Array }> {
  if (!(await isDeviceRememberSupported())) {
    throw new Error('prf_not_supported');
  }

  const enrollment = await readEnrollment(userId);
  if (!enrollment) {
    throw new Error('device_remember_not_enrolled');
  }

  const credentialIdBytes = decodeBase64Url(enrollment.credentialId);
  const prfSalt = decodeBase64Url(enrollment.prfSalt);
  const prfSecret = await assertPasskeyWithPrf(credentialIdBytes, prfSalt);
  let vaultKey: Uint8Array;
  try {
    vaultKey = await unwrapVkFromDevice(
      prfSecret,
      userId,
      enrollment.credentialId,
      decodeBase64Url(enrollment.vkWrap),
    );
  } catch (cause) {
    mapVaultUnlockCryptoFailure(cause);
  }

  return unlockVaultFromKey(vaultKey, vaultBlob, expectedServerVersion);
}

export async function removeDeviceRememberEnrollment(userId: string): Promise<void> {
  await deleteEnrollment(userId);
}

export async function clearDeviceRememberForUser(userId: string): Promise<void> {
  try {
    await deleteEnrollment(userId);
  } catch {
    // Best-effort clear when IndexedDB is unavailable.
  }
}
